const VENDORS: Array<string | null> = [null, 'webkit', 'moz', 'ms', 'o'];

/**
 * @param {string} name
 * @param {string} vendor
 * @returns
 */
function getVendorName(name: string, vendor: string | undefined | null) {
  if (vendor != null && vendor.length > 0) {
    return vendor + name[0].toUpperCase() + name.substr(1);
  } else {
    return name;
  }
}

/**
 * @param {object} obj
 * @param {string} name
 * @returns
 */
function getAttrib(obj: { [key: string]: any; } | null | undefined, name: string) {
  if (obj !== null && obj !== undefined) {
    for (const vendor of VENDORS) {
      const prop = getVendorName(name, vendor);
      if (obj[prop] != null) {
        return obj[prop];
      }
    }
  }
}

/**
 * @param {object} obj
 * @param {string} name
 * @returns
 */
function isPresent(obj: any, name: string) {
  return getAttrib(obj, name) != null;
}

/**
 * @param {WebGLRenderingContext} ctx
 * @param {number} shadertype
 * @param {number} precisiontype
 * @returns
 */
function getFormat(ctx: WebGLRenderingContext, shadertype: number, precisiontype: number) {
  const format = ctx.getShaderPrecisionFormat(shadertype, precisiontype);
  return format && {
    rangeMin: format.rangeMin,
    rangeMax: format.rangeMax,
    precision: format.precision,
  };
}

/**
 * @param {WebGLRenderingContext} ctx
 * @param {number} shadertype
 * @returns
 */
function getStageFormat(ctx: WebGLRenderingContext, shadertype: number) {
  return {
    LOW_FLOAT: getFormat(ctx, shadertype, ctx.LOW_FLOAT),
    MEDIUM_FLOAT: getFormat(ctx, shadertype, ctx.MEDIUM_FLOAT),
    HIGH_FLOAT: getFormat(ctx, shadertype, ctx.HIGH_FLOAT),
    LOW_INT: getFormat(ctx, shadertype, ctx.LOW_INT),
    MEDIUM_INT: getFormat(ctx, shadertype, ctx.MEDIUM_INT),
    HIGH_INT: getFormat(ctx, shadertype, ctx.HIGH_INT),
  };
}

/**
 * @param {WebGLRenderingContext} ctx
 * @returns
 */
function getShaderPrecisions(ctx: WebGLRenderingContext) {
  return {
    VERTEX_SHADER: getStageFormat(ctx, ctx.VERTEX_SHADER),
    FRAGMENT_SHADER: getStageFormat(ctx, ctx.FRAGMENT_SHADER),
  };
}

function isTypedArray(array: any) {
  return array instanceof Int8Array ||
    array instanceof Int16Array ||
    array instanceof Int32Array ||
    array instanceof Uint8Array ||
    array instanceof Uint8ClampedArray ||
    array instanceof Uint16Array ||
    array instanceof Uint32Array ||
    array instanceof Float32Array ||
    array instanceof Float64Array;
}

/**
 * @param {WebGLRenderingContext} ctx
 * @param {string[]} names
 * @returns
 */
function getParams(ctx: WebGLRenderingContext, names: string[]) {
  const params: any = { };
  for (const name of names) {
    // @ts-ignore
    const enumValue: any = ctx[name];
    if (enumValue != null) {
      const param = ctx.getParameter(enumValue);
      if (isTypedArray(param)) {
        // const array = [];
        // for (let j = 0; j < param.length; j++) {
        //   array.push([param[j]]);
        // }
        // params[name] = array;
        params[name] = [...param];
      } else {
        params[name] = param;
      }
    }
  }
  return params;
}

/**
 * @param {WebGLRenderingContext} ctx
 * @returns
 */
function getExtensions(ctx: WebGLRenderingContext) {
  const supported = ctx.getSupportedExtensions() || [];
  const capabilities: any = { };
  for (const extname of supported) {
    const ext = ctx.getExtension(extname);
    if (extname.match('texture_filter_anisotropic')) {
      capabilities[extname] = {
        MAX_TEXTURE_MAX_ANISOTROPY_EXT: ctx.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT),
      };
    } else if (extname.match('OES_standard_derivatives')) {
      capabilities[extname] = {
        FRAGMENT_SHADER_DERIVATIVE_HINT_OES: ctx.getParameter(ext.FRAGMENT_SHADER_DERIVATIVE_HINT_OES),
      };
    } else if (extname.match('WEBGL_draw_buffers')) {
      capabilities[extname] = {
        MAX_COLOR_ATTACHMENTS_WEBGL: ctx.getParameter(ext.MAX_COLOR_ATTACHMENTS_WEBGL),
        MAX_DRAW_BUFFERS_WEBGL: ctx.getParameter(ext.MAX_DRAW_BUFFERS_WEBGL),
      };
    } else if (extname.match('WEBGL_debug_renderer_info')) {
      if (ext != null) {
        capabilities[extname] = {
          UNMASKED_VENDOR_WEBGL: ctx.getParameter(ext.UNMASKED_VENDOR_WEBGL),
          UNMASKED_RENDERER_WEBGL: ctx.getParameter(ext.UNMASKED_RENDERER_WEBGL),
        };
      }
    } else if (extname.match('WEBGL_compressed_texture_astc')) {
      if (ext != null && ext.getSupportedProfiles != null) {
        capabilities[extname] = {
          supportedProfiles: ext.getSupportedProfiles(),
        };
      }
    }
  }

  return {
    supported,
    capabilities,
  };
}

/**
 * @param {object} info
 * @param {HTMLCanvasElement} canvas
 * @param {string} contextId
 * @param {boolean} [perfCaveat=false]
 * @returns {(WebGLRenderingContext | null)}
 */
function getContext(info: any, canvas: HTMLCanvasElement, contextId: string, perfCaveat: boolean = false) {
  try {
    const ctx = canvas.getContext(contextId, {
      stencil: true,
      failIfMajorPerformanceCaveat: perfCaveat,
    });
    if (ctx !== null) {
      info.name = contextId;
      info.supported = true;
    }
    return ctx;
  } catch (error) {
    return null;
  }
}

/**
 * @param {object} info
 * @param {HTMLCanvasElement} canvas
 * @param {string} contextId
 * @param {boolean} [perfCaveat=false]
 * @returns
 */
function getContextByName(info: any, canvas: HTMLCanvasElement, contextId: string, perfCaveat: boolean = false) {
  perfCaveat = perfCaveat || false;
  let ctx = getContext(info, canvas, contextId, perfCaveat);
  if (ctx === null) {
    ctx = getContext(info, canvas, 'experimental-' + contextId, perfCaveat);
  }
  return ctx;
}

/**
 * @param {object} info
 * @param {HTMLCanvasElement} canvas
 * @param {string} contextId
 * @returns
 */
function getCaveatContext(info: any, canvas: HTMLCanvasElement, contextId: string) {
  let ctx: any = getContextByName(info, canvas, contextId, true);
  if (ctx != null) {
    if (ctx.getContextAttributes().failIfMajorPerformanceCaveat != null) {
      info.perfCaveat = false;
    } else {
      info.perfCaveat = null;
    }
    return ctx;
  } else {
    ctx = getContextByName(info, canvas, contextId, false);
    if (ctx != null) {
      info.perfCaveat = true;
      return ctx;
    } else {
      return null;
    }
  }
}

/**
 * @param {WebGLRenderingContext} ctx
 * @returns
 */
function getAntialias(ctx: WebGLRenderingContext) {
  const attributes: any = ctx.getContextAttributes();
  if (!attributes) {
    return false;
  }
  return attributes.antialias;
}

/**
 * @param {string} contextId
 * @param {string[]} paramNames
 * @returns
 */
function getWebglInfo(contextId: string, paramNames: string[]) {
  const canvas = document.createElement('canvas');
  const info: any = { supported: false };
  const ctx = getCaveatContext(info, canvas, contextId);
  if (ctx !== null) {
    ctx.enable(ctx.SAMPLE_ALPHA_TO_COVERAGE);
    ctx.enable(ctx.SAMPLE_COVERAGE);
    info.antialias = getAntialias(ctx);
    info.params = getParams(ctx, paramNames);
    info.extensions = getExtensions(ctx);
    if (ctx.getShaderPrecisionFormat != null) {
      info.shaderPrecision = getShaderPrecisions(ctx);
    } else {
      info.shaderPrecision = null;
    }
  }
  return info;
}

function getEndian() {
  try {
    const buffer = new ArrayBuffer(4);
    const intView = new Uint32Array(buffer);
    const byteView = new Uint8Array(buffer);
    intView[0] = 1;
    if (byteView[0] === 1) {
      return 'little';
    } else {
      return 'big';
    }
  } catch (e) {
    return 'unknown';
  }
}

const WEBGL_PARAM_NAMES = ['ALIASED_LINE_WIDTH_RANGE', 'ALIASED_POINT_SIZE_RANGE', 'ALPHA_BITS', 'BLUE_BITS', 'DEPTH_BITS', 'GREEN_BITS', 'MAX_COMBINED_TEXTURE_IMAGE_UNITS', 'MAX_CUBE_MAP_TEXTURE_SIZE', 'MAX_FRAGMENT_UNIFORM_VECTORS', 'MAX_RENDERBUFFER_SIZE', 'MAX_TEXTURE_IMAGE_UNITS', 'MAX_TEXTURE_SIZE', 'MAX_VARYING_VECTORS', 'MAX_VERTEX_ATTRIBS', 'MAX_VERTEX_TEXTURE_IMAGE_UNITS', 'MAX_VERTEX_UNIFORM_VECTORS', 'MAX_VIEWPORT_DIMS', 'RED_BITS', 'SAMPLE_COVERAGE_VALUE', 'SAMPLES', 'STENCIL_BITS', 'SUBPIXEL_BITS', 'VENDOR', 'RENDERER', 'VERSION', 'SHADING_LANGUAGE_VERSION', 'COMPRESSED_TEXTURE_FORMATS', 'SAMPLE_BUFFERS'];

const WEBGL2_PARAM_NAMES = WEBGL_PARAM_NAMES.concat(['MAX_3D_TEXTURE_SIZE', 'MAX_ARRAY_TEXTURE_LAYERS', 'MAX_COLOR_ATTACHMENTS', 'MAX_COMBINED_FRAGMENT_UNIFORM_COMPONENTS', 'MAX_COMBINED_UNIFORM_BLOCKS', 'MAX_COMBINED_VERTEX_UNIFORM_COMPONENTS', 'MAX_DRAW_BUFFERS', 'MAX_ELEMENT_INDEX', 'MAX_ELEMENTS_INDICES', 'MAX_ELEMENTS_VERTICES', 'MAX_FRAGMENT_INPUT_COMPONENTS', 'MAX_FRAGMENT_UNIFORM_BLOCKS', 'MAX_FRAGMENT_UNIFORM_COMPONENTS', 'MAX_PROGRAM_TEXEL_OFFSET', 'MAX_SAMPLES', 'MAX_SERVER_WAIT_TIMEOUT', 'MAX_TEXTURE_LOD_BIAS', 'MAX_TRANSFORM_FEEDBACK_INTERLEAVED_COMPONENTS', 'MAX_TRANSFORM_FEEDBACK_SEPARATE_ATTRIBS', 'MAX_TRANSFORM_FEEDBACK_SEPARATE_COMPONENTS', 'MAX_UNIFORM_BLOCK_SIZE', 'MAX_UNIFORM_BUFFER_BINDINGS', 'MAX_VARYING_COMPONENTS', 'MAX_VERTEX_OUTPUT_COMPONENTS', 'MAX_VERTEX_UNIFORM_BLOCKS', 'MAX_VERTEX_UNIFORM_COMPONENTS', 'MIN_PROGRAM_TEXEL_OFFSET']);

function getWebGLStat() {
  return {
    endian: getEndian(),
    webgl: getWebglInfo('webgl', WEBGL_PARAM_NAMES),
    webgl2: getWebglInfo('webgl2', WEBGL2_PARAM_NAMES),
    requestAnimationFrame: isPresent(window, 'requestAnimationFrame'),
    fullscreen: isPresent(document, 'cancelFullScreen'),
    pointerlock: isPresent(document.body, 'requestPointerLock'),
    gamepads: isPresent(navigator, 'getGamepads'),
    webAudioData: isPresent(window, 'AudioContext'),
    websocket: isPresent(window, 'WebSocket'),
    webworker: isPresent(window, 'Worker'),
    webRTC: {
      support: isPresent(window, 'RTCPeerConnection'),
      data: isPresent(window, 'RTCDataChannelEvent'),
    },
  };
}

export default getWebGLStat;
